import { type Ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { GOOGLE_SHEETS_DATA_SOURCE_TYPE } from '@/constants/data_sources';
import { usePartnersStore, usePopulationsStore } from '@/stores';
import { Feed } from '@/types/feeds';
import { Population, PopulationStats } from '@/types/populations';
import { Source } from '@/types/sources';

import useOfflinePartner from './useOfflinePartner';

/* A Google Sheet keeps syncing where a CSV is a one-off upload, so it is the
   better default when an offline partner has both */
export function pickDefaultOfflineFeed(feeds: Feed[]) {
  return (
    feeds.find(
      (feed) => feed.integration?.type === GOOGLE_SHEETS_DATA_SOURCE_TYPE,
    ) ?? feeds[0]
  );
}

export function usePopulationModalState() {
  const route = useRoute();
  const router = useRouter();
  const populationsStore = usePopulationsStore();
  const partnersStore = usePartnersStore();
  const { offlinePopulations } = useOfflinePartner();

  const isEditRoute = computed(() => {
    return (
      route.name === 'edit_population' ||
      route.name === 'offline-edit-population' ||
      route.name === 'onboarding-edit-population' ||
      route.name === 'onboarding_review_population'
    );
  });

  const isViewRoute = computed(() => {
    return (
      route.name === 'view_population' ||
      route.name === 'offline-view-population'
    );
  });

  const offlinePartner = computed(() => {
    const partnerId = route.params.partner_org_id as string;
    if (!partnerId) return;
    const numericPartnerId = Number(partnerId);
    if (isNaN(numericPartnerId)) return;
    return partnersStore.offlinePartners.find(
      (partner) => partner.id === numericPartnerId,
    );
  });

  const isOfflinePartner = computed(
    () => !!offlinePartner.value?.offline_partner,
  );

  const offlinePartnerUuid = computed(() => {
    return offlinePartner.value?.uuid;
  });

  const population = computed(() => {
    if (
      (isEditRoute.value || isViewRoute.value) &&
      !!route.params.population_id
    ) {
      const populationId = parseInt(route.params.population_id as string);

      if (isOfflinePartner.value) {
        return (
          offlinePopulations.value.find(
            (p: Population) => p.id === populationId,
          ) || null
        );
      }
      return populationsStore.getPopulationById(populationId) || null;
    }

    return null;
  });

  const isEditMode = computed(() => {
    return isEditRoute.value && !!population.value;
  });

  const isViewMode = computed(() => {
    return isViewRoute.value && !!population.value;
  });

  const isOnboarding = computed(
    () => typeof route.name === 'string' && route.name.includes('onboarding'),
  );

  const isSmartOnboardingRoute = computed(
    () =>
      route.name === 'onboarding_review_population' ||
      route.name === 'onboarding_create_population_smart',
  );

  async function navigateAfterClose() {
    await populationsStore.refreshPopulationsStore();

    if (isSmartOnboardingRoute.value) {
      router.push({ name: 'onboarding' });
    } else if (isOnboarding.value) {
      router.push({ name: 'onboarding_define_populations' });
    } else if (isOfflinePartner.value) {
      router.push({
        name: 'partner_details',
        params: { partner_org_id: route.params.partner_org_id },
      });
    } else {
      router.push({ name: 'populations' });
    }
  }

  async function initializePopulationModal({
    isInitializing,
    mode,
    initializeData,
    resetRefreshPreviewFlag,
    selectedFeedId,
    currentFeeds,
    completeFilterSources,
    getFilteredSources,
    currentSources,
    fetchFilterData,
    initializeFilterGroups,
  }: {
    isInitializing: Ref<boolean>;
    mode: Ref<'view' | 'edit' | 'create'>;
    initializeData: () => Promise<void>;
    resetRefreshPreviewFlag: () => void;
    selectedFeedId: Ref<number | null | 'add-new-source'>;
    currentFeeds: Ref<Feed[]>;
    completeFilterSources: Ref<Source[]>;
    getFilteredSources: (feedId: number) => Source[];
    currentSources: Ref<Source[]>;
    fetchFilterData: (
      population:
        | (Population & {
            meta?: PopulationStats;
            isEmpty?: boolean;
            tag?: string;
          })
        | null,
      sources: Source[],
      offlinePartnerUuid?: string,
    ) => Promise<void>;
    initializeFilterGroups: (
      population:
        | (Population & {
            meta?: PopulationStats;
            isEmpty?: boolean;
            tag?: string;
          })
        | null,
      completeFilterSources: Source[],
    ) => void;
  }) {
    const hasExistingPopulation =
      population.value &&
      !(population.value as Population & { isEmpty?: boolean }).isEmpty;

    if (hasExistingPopulation && (isEditMode.value || mode.value === 'view')) {
      isInitializing.value = true;
    }

    try {
      await initializeData();
      resetRefreshPreviewFlag();

      const defaultOfflineFeed = isOfflinePartner.value
        ? pickDefaultOfflineFeed(currentFeeds.value)
        : undefined;
      if (defaultOfflineFeed && !selectedFeedId.value) {
        selectedFeedId.value = defaultOfflineFeed.id;
        completeFilterSources.value = getFilteredSources(defaultOfflineFeed.id);
      }

      if (
        population.value &&
        !(population.value as Population & { isEmpty?: boolean }).isEmpty &&
        selectedFeedId.value &&
        population.value.source_id
      ) {
        if (!completeFilterSources.value.length) {
          await fetchFilterData(
            population.value,
            currentSources.value,
            offlinePartnerUuid.value,
          );
        }
        if (
          typeof selectedFeedId.value === 'number' &&
          !completeFilterSources.value.length
        ) {
          completeFilterSources.value = getFilteredSources(
            selectedFeedId.value,
          );
        }
        initializeFilterGroups(population.value, completeFilterSources.value);
      }
    } finally {
      isInitializing.value = false;
    }
  }

  return {
    route,
    router,
    isEditRoute,
    offlinePartner,
    isOfflinePartner,
    offlinePartnerUuid,
    population,
    isEditMode,
    isViewMode,
    isOnboarding,
    navigateAfterClose,
    initializePopulationModal,
  };
}
